import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WebinarSection = () => {
  const [activeTab, setActiveTab] = useState('upcoming');

  const upcomingWebinars = [
    {
      id: 'webinar-1',
      title: 'Building AI Independence: A Strategic Roadmap for 2025',
      description: 'Join our CEO and leading AI strategists as they outline the critical steps for achieving AI independence in the coming year.',
      date: '2025-01-15',
      time: '2:00 PM EST',
      duration: '60 minutes',
      speakers: ['Sarah Chen, CEO', 'Dr. Michael Rodriguez, CTO'],
      attendees: 847,
      maxAttendees: 1000,
      tags: ['Strategy', 'Leadership', 'Planning']
    },
    {
      id: 'webinar-2',
      title: 'Open-Source AI Tools: Technical Deep Dive',
      description: 'Technical session covering the latest open-source AI frameworks and their enterprise implementation strategies.',
      date: '2025-01-22',
      time: '3:00 PM EST',
      duration: '90 minutes',
      speakers: ['Alex Thompson, Lead Engineer', 'Dr. Lisa Wang, AI Researcher'],
      attendees: 623,
      maxAttendees: 500,
      tags: ['Technical', 'Implementation', 'Tools']
    },
    {
      id: 'webinar-3',
      title: 'ROI of AI Independence: Financial Analysis Workshop',
      description: 'Comprehensive workshop on calculating and presenting the financial benefits of open-source AI adoption to stakeholders.',
      date: '2025-01-29',
      time: '1:00 PM EST',
      duration: '75 minutes',
      speakers: ['David Park, Financial Analyst', 'Jennifer Liu, Business Strategist'],
      attendees: 412,
      maxAttendees: 750,
      tags: ['Finance', 'ROI', 'Business Case']
    }
  ];

  const pastWebinars = [
    {
      id: 'past-1',
      title: 'Vendor Lock-in: The Hidden Costs of AI Dependency',
      description: 'Analysis of real-world cases where vendor lock-in created significant business risks and how to avoid them.',
      date: '2024-12-18',
      duration: '60 minutes',
      views: 2847,
      rating: 4.8,
      tags: ['Risk Management', 'Case Studies']
    },
    {
      id: 'past-2',
      title: 'Open-Source AI Security: Enterprise Best Practices',
      description: 'Comprehensive guide to securing open-source AI implementations in enterprise environments.',
      date: '2024-12-11',
      duration: '75 minutes',
      views: 1923,
      rating: 4.9,
      tags: ['Security', 'Best Practices']
    },
    {
      id: 'past-3',
      title: 'AI Transformation Success Stories: Healthcare Edition',
      description: 'Real transformation stories from healthcare organizations that successfully implemented open-source AI solutions.',
      date: '2024-12-04',
      duration: '90 minutes',
      views: 3156,
      rating: 4.7,
      tags: ['Healthcare', 'Case Studies', 'Success Stories']
    }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getAvailabilityStatus = (attendees, maxAttendees) => {
    const percentage = (attendees / maxAttendees) * 100;
    if (percentage >= 90) return { status: 'Almost Full', color: 'text-red-600 bg-red-50' };
    if (percentage >= 70) return { status: 'Filling Fast', color: 'text-orange-600 bg-orange-50' };
    return { status: 'Available', color: 'text-green-600 bg-green-50' };
  };

  return (
    <section className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          AI Independence Webinar Series
        </h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          Join industry experts and thought leaders in our comprehensive webinar series on achieving AI independence
        </p>
      </div>
      {/* Tab Navigation */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'upcoming' ?'bg-white text-brand-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Upcoming Webinars
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'past' ?'bg-white text-brand-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Past Recordings
          </button>
        </div>
      </div>
      {/* Upcoming Webinars */}
      {activeTab === 'upcoming' && (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {upcomingWebinars?.map((webinar) => {
            const availability = getAvailabilityStatus(webinar?.attendees, webinar?.maxAttendees);
            return (
              <div
                key={webinar?.id}
                className="bg-white rounded-lg shadow-executive border border-gray-200 hover:shadow-executive-lg transition-all duration-300"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-orange-50 rounded-lg">
                      <Icon name="Video" size={24} className="text-orange-600" />
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${availability?.color}`}>
                      {availability?.status}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      {webinar?.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-4">
                      {webinar?.description}
                    </p>

                    {/* Date & Time */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-text-secondary">
                        <Icon name="Calendar" size={16} className="mr-2" />
                        <span>{formatDate(webinar?.date)}</span>
                      </div>
                      <div className="flex items-center text-sm text-text-secondary">
                        <Icon name="Clock" size={16} className="mr-2" />
                        <span>{webinar?.time} • {webinar?.duration}</span>
                      </div>
                    </div>

                    {/* Speakers */}
                    <div className="mb-4">
                      <div className="flex items-center text-sm text-text-secondary mb-2">
                        <Icon name="Users" size={16} className="mr-2" />
                        <span>Speakers:</span>
                      </div>
                      <div className="text-sm text-text-primary">
                        {webinar?.speakers?.join(', ')}
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {webinar?.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Attendance */}
                    <div className="text-sm text-text-secondary mb-4">
                      {webinar?.attendees} / {webinar?.maxAttendees} registered
                    </div>
                  </div>

                  {/* Action */}
                  <Button
                    variant="default"
                    fullWidth
                    className="bg-brand-primary hover:bg-brand-primary/90"
                    iconName="Calendar"
                    iconPosition="right"
                  >
                    Register Now
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Past Webinars */}
      {activeTab === 'past' && (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {pastWebinars?.map((webinar) => (
            <div
              key={webinar?.id}
              className="bg-white rounded-lg shadow-executive border border-gray-200 hover:shadow-executive-lg transition-all duration-300"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <Icon name="Play" size={24} className="text-purple-600" />
                  </div>
                  <div className="flex items-center space-x-1 text-sm text-text-secondary">
                    <Icon name="Star" size={16} className="text-yellow-500" />
                    <span>{webinar?.rating}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    {webinar?.title}
                  </h3>
                  <p className="text-text-secondary text-sm mb-4">
                    {webinar?.description}
                  </p>

                  {/* Meta Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="Calendar" size={16} className="mr-2" />
                      <span>{formatDate(webinar?.date)}</span>
                    </div>
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="Clock" size={16} className="mr-2" />
                      <span>{webinar?.duration}</span>
                    </div>
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="Eye" size={16} className="mr-2" />
                      <span>{webinar?.views?.toLocaleString()} views</span>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {webinar?.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action */}
                <Button
                  variant="default"
                  fullWidth
                  className="bg-brand-primary hover:bg-brand-primary/90"
                  iconName="Play"
                  iconPosition="right"
                >
                  Watch Recording
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default WebinarSection;